/**
 * rrweb recording + chunked replay upload for the Observ RUM SDK (FR-2, story 2.2).
 *
 * Captures the visual session with `@rrweb/record` (initial FullSnapshot + DOM
 * mutations), buffers events in memory, and POSTs gzip chunks to `/v1/replay`
 * (story 2.1) — triggered by SIZE or TIME, whichever comes first. The heavy flux
 * uses this channel exclusively, never the OTLP exporter.
 *
 * Invariant (1.1/1.2/1.3): the SDK must NEVER break the host page. Every path —
 * capture, serialize, gzip, upload — is defensive: a failure degrades to "no
 * replay", never an exception that escapes to the page.
 *
 * Flushes are SERIALIZED through a promise chain (`pending`): every trigger is
 * queued — never dropped — so the buffer cannot grow unbounded behind an
 * in-flight flush, `seq` is assigned in order, and two uploads never overlap.
 *
 * Session activity (1.2 deferral): `getSessionId` is read once per flush (~5 s),
 * NOT per rrweb event — touching the session on every high-frequency mutation
 * would thrash `sessionStorage` and jank the main thread.
 */
import { record } from '@rrweb/record'

import { resolveReplayMasking } from './privacy.js'
import { getSessionId } from './session.js'
import { buildReplayUrl, gzipJsonl, postReplayChunk } from './transport.js'
import type { ObservInitOptions } from './types.js'

/** Serialized buffer size (pre-gzip) that triggers a size-based flush. */
export const CHUNK_MAX_BYTES = 512 * 1024 // ~512 KiB — well under the 16 MiB cap
/** Interval (ms) for the time-based flush. */
export const CHUNK_INTERVAL_MS = 5_000
/** rrweb full-snapshot cadence so a reader can resync from a checkpoint. */
export const CHECKOUT_INTERVAL_MS = 5 * 60 * 1_000

/** Public handle for the running recorder. */
export interface ReplayRecorder {
  /** Stop capture, remove listeners, and flush the residual. Idempotent. */
  stop(): Promise<void>
}

/** No-op recorder returned in a non-DOM environment (SSR) — nothing runs. */
const NOOP_RECORDER: ReplayRecorder = { stop: () => Promise.resolve() }

/**
 * Start rrweb capture + chunked upload. Returns a handle whose `stop()` tears
 * everything down and flushes the tail. Never throws: a capture/start failure is
 * swallowed (the page keeps working, just without replay).
 */
export function startReplayRecording(options: ObservInitOptions): ReplayRecorder {
  // SSR / non-DOM guard: rrweb and the lifecycle listeners need `document` /
  // `window`. Degrade to a no-op recorder (mirrors session.ts's globalThis-
  // defensive style) rather than throwing a ReferenceError / leaking a timer.
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return NOOP_RECORDER
  }

  const url = buildReplayUrl(options.endpoint)
  const key = options.key

  let buffer: string[] = []
  let bufferedBytes = 0
  let seq = 0
  let lastSessionId: string | null = null
  let stopped = false
  // Flush chain: each trigger appends to this promise so flushes run one after
  // another (never concurrently, never dropped). Kept reject-free by the inner
  // try/catch + the trailing `.catch`.
  let pending: Promise<void> = Promise.resolve()

  /** Serialize → gzip → upload the current buffer. Swapped before the first await. */
  async function doFlush(keepalive: boolean): Promise<void> {
    if (buffer.length === 0) return
    const lines = buffer
    buffer = []
    bufferedBytes = 0
    try {
      const body = await gzipJsonl(lines)
      const sessionId = getSessionId()
      // The session id rotated (idle tab past the 30-min window): restart `seq`
      // so the new object-store prefix begins at 0 (its own checkpoint).
      if (lastSessionId !== null && lastSessionId !== sessionId) seq = 0
      lastSessionId = sessionId
      await postReplayChunk({ url, key, sessionId, seq: seq++, body, keepalive })
    } catch (err) {
      console.warn('[observ] replay flush failed', err)
    }
  }

  /** Queue a flush after any in-flight one (never dropped). */
  function enqueueFlush(keepalive = false): void {
    pending = pending.then(() => doFlush(keepalive)).catch(() => {})
  }

  const emit = (event: unknown): void => {
    if (stopped) return // drop trailing events after teardown
    try {
      const line = JSON.stringify(event)
      buffer.push(line)
      bufferedBytes += line.length
      if (bufferedBytes >= CHUNK_MAX_BYTES) enqueueFlush()
    } catch {
      /* never let capture break the host page */
    }
  }

  // PII masking (story 4.1): safe-by-default — input values are masked unless
  // the host explicitly opts out via `privacy.maskAllInputs: false`. Resolved
  // once here and spread into the recorder config so the heavy replay flux is
  // privacy-safe by construction (architecture D7 / PRD Open Q5).
  const masking = resolveReplayMasking(options.privacy)

  let stopFn: (() => void) | undefined
  try {
    // `record` returns the stop handler (or undefined if unsupported).
    stopFn = record({ emit, checkoutEveryNms: CHECKOUT_INTERVAL_MS, ...masking }) ?? undefined
  } catch (err) {
    console.warn('[observ] rrweb recording failed to start', err)
  }

  const interval = setInterval(() => enqueueFlush(), CHUNK_INTERVAL_MS)

  // Lifecycle flush (1.3 deferral). visibilitychange→hidden fires on tab
  // switch / minimize / most mobile navigations while the page is still alive;
  // pagehide is the best-effort last resort (keepalive — a body the browser
  // can't keepalive-send is rejected and logged, not silently pre-dropped).
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') enqueueFlush()
  }
  const onPageHide = (): void => {
    enqueueFlush(true)
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onPageHide)

  return {
    async stop(): Promise<void> {
      if (stopped) return
      stopped = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      try {
        stopFn?.()
      } catch {
        /* swallow — teardown must not throw */
      }
      enqueueFlush() // final residual
      await pending // wait for the whole chain (incl. the final flush) to settle
    },
  }
}
